// src/features/trainings/components/AssignParticipantsComponent.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form, Button, ListGroup, InputGroup, Spinner } from 'react-bootstrap';
import { FaSearch, FaArrowRight, FaArrowLeft, FaSave, FaTimes } from 'react-icons/fa';

// Servicios
import PeopleService from '../../people/PeopleService';

/**
 * Componente modal para asignar participantes a una capacitación
 * 
 * @param {boolean} show - Indica si el modal está visible
 * @param {Function} onHide - Función para cerrar el modal
 * @param {Function} onSave - Callback cuando se guardan los cambios
 * @param {Array} selectedPersons - Lista de personas ya seleccionadas
 */
function AssignParticipantsComponent({ show, onHide, onSave, selectedPersons = [] }) {
  // Estados
  const [loading, setLoading] = useState(true);
  const [savingChanges, setSavingChanges] = useState(false);
  const [allPersons, setAllPersons] = useState([]);
  const [assignedPersons, setAssignedPersons] = useState([]);
  const [unassignedPersons, setUnassignedPersons] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [filterAssigned, setFilterAssigned] = useState('');
  const [filterUnassigned, setFilterUnassigned] = useState('');

  // Función para normalizar texto removiendo acentos/tildes
  const normalizeText = (text) => {
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase();
  };

  // Cargar personas al iniciar
  useEffect(() => {
    loadPersons();
  }, []);

  // Cargar personas y clasificarlas
  const loadPersons = async () => {
    try {
      setLoading(true);
      
      // Obtener todas las personas (solo físicas, no jurídicas)
      const personsData = await PeopleService.getAllPeople();
      
      // Filtrar solo personas físicas (no empresas)
      const filteredPersons = personsData.filter(person => 
        person.type === 'F' && person.regime !== 'Cliente'
      );
      
      setAllPersons(filteredPersons);
      
      // Clasificar en asignados / no asignados
      const selectedIds = selectedPersons.map(p => p.id);
      
      const assigned = filteredPersons.filter(person => 
        selectedIds.includes(person.id)
      );
      
      const unassigned = filteredPersons.filter(person => 
        !selectedIds.includes(person.id)
      );
      
      setAssignedPersons(assigned);
      setUnassignedPersons(unassigned);
    } catch (error) {
      console.error('Error al cargar personas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar lista de personas asignadas
  const filteredAssignedPersons = assignedPersons.filter(person => {
    if (!filterAssigned) return true;
    
    const normalizedSearchTerm = normalizeText(filterAssigned);
    const fullName = `${person.firstName || ''} ${person.surname || ''}`;
    const normalizedFullName = normalizeText(fullName);
    const normalizedDocument = normalizeText(person.document || '');
    
    return normalizedFullName.includes(normalizedSearchTerm) || 
           normalizedDocument.includes(normalizedSearchTerm);
  });

  // Filtrar lista de personas no asignadas
  const filteredUnassignedPersons = unassignedPersons.filter(person => {
    if (!filterUnassigned) return true;
    
    const normalizedSearchTerm = normalizeText(filterUnassigned);
    const fullName = `${person.firstName || ''} ${person.surname || ''}`;
    const normalizedFullName = normalizeText(fullName);
    const normalizedDocument = normalizeText(person.document || '');
    
    return normalizedFullName.includes(normalizedSearchTerm) || 
           normalizedDocument.includes(normalizedSearchTerm);
  });

  // Asignar personas seleccionadas
  const handleAssignPersons = () => {
    if (selectedUnassigned.length === 0) return;
    
    // Encontrar las personas seleccionadas
    const personsToAssign = unassignedPersons.filter(person => 
      selectedUnassigned.includes(person.id)
    );
    
    // Mover a lista de asignados
    setAssignedPersons([...assignedPersons, ...personsToAssign]);
    
    // Eliminar de lista de no asignados
    setUnassignedPersons(unassignedPersons.filter(person => 
      !selectedUnassigned.includes(person.id)
    ));
    
    // Limpiar selección
    setSelectedUnassigned([]);
  };

  // Desasignar personas seleccionadas
  const handleUnassignPersons = () => {
    if (selectedAssigned.length === 0) return;
    
    // Encontrar las personas seleccionadas
    const personsToUnassign = assignedPersons.filter(person => 
      selectedAssigned.includes(person.id)
    );
    
    // Mover a lista de no asignados
    setUnassignedPersons([...unassignedPersons, ...personsToUnassign]);
    
    // Eliminar de lista de asignados
    setAssignedPersons(assignedPersons.filter(person => 
      !selectedAssigned.includes(person.id)
    ));
    
    // Limpiar selección
    setSelectedAssigned([]);
  };

  // Manejar selección en lista de asignados
  const handleSelectAssigned = (personId) => {
    // Verificar si ya está seleccionada
    if (selectedAssigned.includes(personId)) {
      // Quitar de selección
      setSelectedAssigned(selectedAssigned.filter(id => id !== personId));
    } else {
      // Agregar a selección
      setSelectedAssigned([...selectedAssigned, personId]);
    }
  };

  // Manejar selección en lista de no asignados
  const handleSelectUnassigned = (personId) => {
    // Verificar si ya está seleccionada
    if (selectedUnassigned.includes(personId)) {
      // Quitar de selección
      setSelectedUnassigned(selectedUnassigned.filter(id => id !== personId));
    } else {
      // Agregar a selección
      setSelectedUnassigned([...selectedUnassigned, personId]);
    }
  };

  // Guardar cambios y llamar al callback
  const handleSaveChanges = () => {
    setSavingChanges(true);
    
    // Llamar al callback con las personas asignadas
    if (onSave) {
      onSave(assignedPersons);
    }
    
    setSavingChanges(false);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Asignar Participantes a la Capacitación</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
            <p className="mt-2">Cargando lista de personas...</p>
          </div>
        ) : (
          <>
            <p className="text-muted mb-3">
              Seleccione las personas que participarán en esta capacitación.
            </p>
            
            <Row>
              {/* Lista de personas no asignadas */}
              <Col md={5}>
                <h6 className="mb-2">Personas Disponibles</h6>
                
                <InputGroup className="mb-2">
                  <Form.Control
                    placeholder="Buscar por nombre o documento..."
                    value={filterUnassigned}
                    onChange={(e) => setFilterUnassigned(e.target.value)}
                  />
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup>
                
                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
                  <ListGroup variant="flush">
                    {filteredUnassignedPersons.length === 0 ? (
                      <ListGroup.Item className="text-center text-muted py-4">
                        No hay personas disponibles
                      </ListGroup.Item>
                    ) : (
                      filteredUnassignedPersons.map(person => (
                        <ListGroup.Item 
                          key={person.id}
                          action
                          active={selectedUnassigned.includes(person.id)}
                          onClick={() => handleSelectUnassigned(person.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex justify-content-between">
                            <div>
                              {person.firstName} {person.surname || ''}
                            </div>
                            <div className="text-muted">
                              {person.document || ''}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </div>
              </Col>
              
              {/* Botones de acción */}
              <Col md={2} className="d-flex flex-column align-items-center justify-content-center">
                <Button 
                  variant="primary"
                  size="sm"
                  className="mb-2 w-75"
                  onClick={handleAssignPersons}
                  disabled={selectedUnassigned.length === 0}
                >
                  <FaArrowRight className="me-1" /> Asignar
                </Button>
                
                <Button 
                  variant="primary"
                  size="sm"
                  className="mb-2 w-75"
                  onClick={handleUnassignPersons}
                  disabled={selectedAssigned.length === 0}
                >
                  <FaArrowLeft className="me-1" /> Desasignar
                </Button>
              </Col>
              
              {/* Lista de personas asignadas */}
              <Col md={5}>
                <h6 className="mb-2">Personas Asignadas ({assignedPersons.length})</h6>
                
                <InputGroup className="mb-2">
                  <Form.Control
                    placeholder="Buscar por nombre o documento..."
                    value={filterAssigned}
                    onChange={(e) => setFilterAssigned(e.target.value)}
                  />
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup>
                
                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
                  <ListGroup variant="flush">
                    {filteredAssignedPersons.length === 0 ? (
                      <ListGroup.Item className="text-center text-muted py-4">
                        No hay personas asignadas
                      </ListGroup.Item>
                    ) : (
                      filteredAssignedPersons.map(person => (
                        <ListGroup.Item 
                          key={person.id}
                          action
                          active={selectedAssigned.includes(person.id)}
                          onClick={() => handleSelectAssigned(person.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex justify-content-between">
                            <div>
                              {person.firstName} {person.surname || ''}
                            </div>
                            <div className="text-muted">
                              {person.document || ''}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary"
          onClick={onHide}
          disabled={savingChanges}
        >
          <FaTimes className="me-1" /> Cancelar
        </Button>
        <Button 
          variant="primary"
          onClick={handleSaveChanges}
          disabled={savingChanges || loading}
        >
          {savingChanges ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Guardando...
            </>
          ) : (
            <>
              <FaSave className="me-1" /> Modificar Participantes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignParticipantsComponent;